import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { FILTER_OPTIONS } from "../constants";
import { changeOrganization } from "../redux/organizationsSagas";
import { organizationsApi } from "../api";
import {
  filterByDefault,
  filterByValue,
  mapByName,
  schemaResolver,
  unregisterFields
} from "../utils";

export const useOrganizationEdit = ({ id }) => {
  const dispatch = useDispatch();

  const { roles, locationalOverviewReportMetrics, dateFormats, timeFormats } = useSelector(
    (state) => state.organizations.commonSettings
  );

  const [currentOrganizationInfo, setCurrentOrganizationInfo] = useState({});

  const getDefaultValues = async () => {
    let { data } = await organizationsApi.getOrganizationInfo({ params: { id: id.toString() } });

    const {
      businessName,
      inactive,
      activeYears,
      deactivateOn,
      currency,
      dateFormat,
      timeFormat,
      defaultClientRoleId,
      beginningOfWeek,
      clientName,
      inactiveUsers,
      use2fa,
      availableGoals,
      availableStaffGoals,
      defaultDashboardModules,
      defaultRevenueCategoriesOnTotalRevenueModule,
      useSecondaryRevenueCategories,
      clientFirstVisitClassNames,
      zeroRetention,
      exclude_client_prospect_types,
      countSameDayConversion,
      defaultConversionRevenueTypes,
      showFutureAppointmentsOnFunnelLaser,
      showFutureAppointmentsByDefault,
      calculateHistoricalData,
      calculateAutopaySchedules,
      calculateChallengers,
      showLaserNetworkAverages,
      leaderboardDetails,
      daysFromTheLastVisit,
      daysPreviousToTheLastVisit,
      availablePages,
      locationalOverviewReportMetrics: selectedLocationalOverviewReportMetrics,
      dailyEmailNotifierClass,
      sendDailyReportEmails,
      includeCrossRegionalVisits,
      startFromAppointmentByDefault,
      excludePreconvertedClientsFromAppointments,
      exclude_frozen_members_from_total_members_count,
      attritionRateLogic,
      dailyPulseAttritionRateLogic,
      membershipCalculationAgreementTypes,
      membershipMetric: selectedMembershipMetric,
      agreementNamesWithZeroSales,
      membershipStart
    } = data;

    const defaultValues = {
      business_name: businessName,
      inactive,
      active_years: activeYears,
      deactivate_on: deactivateOn,
      currency,
      date_format: filterByValue(dateFormats, dateFormat),
      time_format: filterByValue(timeFormats, timeFormat),
      default_client_role_id: filterByValue(roles, defaultClientRoleId),
      beginning_of_week: filterByValue(FILTER_OPTIONS.beginningOfWeek, beginningOfWeek),

      client_name: clientName,
      inactive_clients: inactiveUsers,
      use_2fa: use2fa,

      available_goals: mapByName(availableGoals),
      available_staff_goals: mapByName(availableStaffGoals),

      default_dashboard_modules: filterByDefault(defaultDashboardModules),

      default_revenue_type_ids: filterByDefault(defaultRevenueCategoriesOnTotalRevenueModule),
      use_secondary_revenue_categories: useSecondaryRevenueCategories,

      client_first_visit_class_names: clientFirstVisitClassNames,
      exclude_client_prospect_types,
      zero_retention: zeroRetention,
      count_same_day_conversion: countSameDayConversion,
      default_conversion_revenue_types: mapByName(defaultConversionRevenueTypes),
      show_future_appointments_on_funnel_laser: showFutureAppointmentsOnFunnelLaser,
      show_future_appointments_by_default: showFutureAppointmentsByDefault,

      calculate_historical_data: calculateHistoricalData,
      calculate_autopay_schedules: calculateAutopaySchedules,
      calculate_challengers: calculateChallengers,
      show_laser_network_averages: showLaserNetworkAverages,
      leaderboard_details: leaderboardDetails,
      risk_members_days_from_last_visit: daysFromTheLastVisit,
      risk_members_days_prev_last_visit: daysPreviousToTheLastVisit,

      available_pages: mapByName(availablePages),
      locational_overview_report_metrics: locationalOverviewReportMetrics.filter((item) =>
        selectedLocationalOverviewReportMetrics.includes(item.id)
      ),

      daily_email_notifier_class: dailyEmailNotifierClass,
      send_daily_report_emails: sendDailyReportEmails,
      include_cross_regional_visits: includeCrossRegionalVisits,
      start_from_appointment_by_default: startFromAppointmentByDefault,
      exclude_preconverted_clients_from_appointments: excludePreconvertedClientsFromAppointments,
      exclude_frozen_members_from_total_members_count:
        exclude_frozen_members_from_total_members_count,
      attrition_rate_logic: filterByValue(FILTER_OPTIONS.attritionRateLogic, attritionRateLogic),
      daily_pulse_attrition_rate_logic: filterByValue(
        FILTER_OPTIONS.attritionRateLogic,
        dailyPulseAttritionRateLogic
      ),
      membership_calculation_agreement_types: mapByName(membershipCalculationAgreementTypes),

      membership_metric: filterByValue(FILTER_OPTIONS.membershipMetrics, selectedMembershipMetric),
      agreement_names_with_zero_sales: filterByDefault(agreementNamesWithZeroSales),
      membership_start: filterByValue(FILTER_OPTIONS.membershipStart, membershipStart)
    };

    setCurrentOrganizationInfo({
      agreementNamesWithZeroSales,
      defaultRevenueCategoriesOnTotalRevenueModule
    });

    return defaultValues;
  };

  const form = useForm({
    resolver: schemaResolver,
    defaultValues: getDefaultValues
  });

  let {
    handleSubmit,
    watch,
    unregister,
    formState: { isLoading, dirtyFields }
  } = form;

  const watchedValues = watch(["show_future_appointments_on_funnel_laser", "membership_metric"]);
  const [showFutureAppointments, membershipMetric] = watchedValues;

  const onSubmit = async (data) => {
    dispatch(changeOrganization({ id, data, dirtyFields }));
  };

  useEffect(() => {
    if (isLoading) return;

    unregisterFields({ unregister, showFutureAppointments, membershipMetric });

    if (
      membershipMetric.id === "NumberOfMembersByProfile" ||
      membershipMetric.id === "NumberOfMembersByStatus"
    ) {
      unregister("agreement_names_with_zero_sales", { keepDefaultValue: true });
    }
  }, [showFutureAppointments, membershipMetric]);

  return {
    ...form,
    watchedValues,
    submit: handleSubmit(onSubmit),
    currentOrganizationInfo,
    setCurrentOrganizationInfo
  };
};
